import React, {useEffect, useMemo, useState} from "react";
import {Bookmark} from "lucide-react";
import {toast} from "sonner";

import {AjaxSeriesRestControllerApi, DiscussionWithoutContentDTO} from "@/generated-client";
import {ClientFactory} from "@/main/common/ClientFactory";
import {CtaToggle} from "@/main/react/buttons/CtaButton";
import {ACCOUNT_ACCESS_DIALOG_COPY, withAccountAccessSurface} from "@/main/react/account_access/AccountAccessDialogContext";
import {isBookmarksSeries} from "@/main/react/series/BookmarksSeriesLocalization";
import {useViewerActionRecord} from "@/main/common/ViewerActionRecords";
import {
    emitDiscussionAddedToSeries,
    emitDiscussionRemovedFromSeries,
    emitSeriesAddedToDiscussion,
    emitSeriesRemovedFromDiscussion,
    onSeriesAddedToDiscussion,
    onSeriesRemovedFromDiscussion,
} from "@/main/common/EventRegistry";
import {trackBookmarkAdded, trackBookmarkRemoved} from "@/main/analytics/analyticsEvents";
import {Localizer} from "@/main/react/Types";
import logger from "@/lib/logger";

type Props = {
    discussionPublicId: string;
    discussionSlug?: string;
    initiallySaved?: boolean;
    localizer: Localizer;
    /** Optional extra classes for the underlying toggle (e.g. compact rail sizing). */
    extraClass?: string;
    /** Show the label text next to the icon (defaults to icon-only, matching the discussion bar). */
    showText?: boolean;
    playwrightId?: string;
};

/**
 * Standalone bookmark toggle for a discussion, reusable outside the full DiscussionButtons bar
 * (e.g. compact recommendation-rail rows). Mirrors the bookmark logic in DiscussionButtons:
 * optimistic UI, the shared bookmarks-series API, EventRegistry sync so every mounted instance
 * (rail row + discussion page) stays consistent, analytics, and toasts.
 */
export default function DiscussionBookmarkToggle(props: Props) {
    const bookmarksApi: AjaxSeriesRestControllerApi = useMemo(() => ClientFactory.getAjaxSeriesRestControllerApi(), []);
    const {localizer} = props;

    // Same kind and same resource id as `DiscussionButtons` uses, deliberately: the bookmark is ONE
    // action with two controls, and holding it in two independent `useState`s is how they disagree
    // after a Back. See `ViewerActionRecords`. The hook also owns the prop resync that used to live
    // here as a blind `setIsSaved(props.initiallySaved)`, which is defect path 2 in that module.
    const [isSaved, setIsSaved] = useViewerActionRecord(
        "discussion-bookmark", props.discussionPublicId, props.initiallySaved ?? false,
    );
    const [saving, setSaving] = useState(false);

    const saveAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.save, "discussion_rail_save");

    useEffect(() => {
        if (!props.discussionPublicId) return;

        const unsubscribeAdded = onSeriesAddedToDiscussion((payload) => {
            if (!payload || payload.discussionPublicId !== props.discussionPublicId || !payload.series) {
                return;
            }
            if (isBookmarksSeries(payload.series as any)) {
                setIsSaved(true);
            }
        });

        const unsubscribeRemoved = onSeriesRemovedFromDiscussion((payload) => {
            if (!payload || payload.discussionPublicId !== props.discussionPublicId || !payload.series) {
                return;
            }
            if (isBookmarksSeries(payload.series as any)) {
                setIsSaved(false);
            }
        });

        return () => {
            unsubscribeAdded();
            unsubscribeRemoved();
        };
    }, [props.discussionPublicId]);

    const toggle = async () => {
        if (saving || !props.discussionPublicId) {
            return;
        }

        const currentlySaved = isSaved;
        setSaving(true);
        setIsSaved(!currentlySaved);

        try {
            const {data: bookmarksSeries} = currentlySaved
                ? await bookmarksApi.removeDiscussionFromBookmarks(props.discussionPublicId)
                : await bookmarksApi.addDiscussionToBookmarks(props.discussionPublicId);
            const discussionPayload: DiscussionWithoutContentDTO = {
                publicId: props.discussionPublicId,
                slug: props.discussionSlug,
            };

            if (bookmarksSeries?.publicId) {
                if (currentlySaved) {
                    emitDiscussionRemovedFromSeries({
                        seriesPublicId: bookmarksSeries.publicId,
                        discussion: discussionPayload,
                        series: bookmarksSeries,
                    });
                    emitSeriesRemovedFromDiscussion({
                        discussionPublicId: props.discussionPublicId,
                        series: bookmarksSeries,
                    });
                } else {
                    emitDiscussionAddedToSeries({
                        seriesPublicId: bookmarksSeries.publicId,
                        discussion: discussionPayload,
                        series: bookmarksSeries,
                    });
                    emitSeriesAddedToDiscussion({
                        discussionPublicId: props.discussionPublicId,
                        series: bookmarksSeries,
                    });
                }
            }

            if (currentlySaved) {
                trackBookmarkRemoved("discussion");
            } else {
                trackBookmarkAdded("discussion");
            }

            toast.success(localizer(currentlySaved
                ? "typescript.discussion.bookmarks.removed"
                : "typescript.discussion.bookmarks.saved"));
        } catch (error) {
            setIsSaved(currentlySaved);
            logger.error("Bookmarks save update failed", error);
            toast.error(localizer("typescript.discussion.bookmarks.update_failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <CtaToggle
            icon={Bookmark}
            variant="ghost"
            size="sm"
            requiresLogin={true}
            accountAccessDialog={saveAccountAccessDialog}
            buttonActive={isSaved}
            onPressedChange={() => void toggle()}
            disabled={saving}
            isLoading={saving}
            extraClass={props.extraClass ?? "discussion-save-bookmarks-button"}
            interactionEffect="bookmark"
            playwrightId={props.playwrightId ?? "discussion-bookmark-toggle"}
            title={localizer(isSaved ? "typescript.discussion.saved" : "typescript.discussion.save")}
            text={props.showText ? localizer(isSaved ? "typescript.discussion.saved" : "typescript.discussion.save") : undefined}
            displayTextOnMobile={false}
        />
    );
}
